import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import type { components } from "@dolfin/api-admin";
import { InfoHint } from "@dolfin/ui";
import {
  BILL_APPROVAL_TEMPLATE_KEY, WHATSAPP_EVENTS, WHATSAPP_QUALITY, WHATSAPP_STAGES as stages, WHATSAPP_TEMPLATE_STATUS,
  WHATSAPP_TONES as tones, arTemplateKey, isInternationalNumber, ownLinkTemplateKey, useEmbeddedSignup, whatsAppEventReview,
  whatsAppTemplateLabel as templateLabel, whatsAppTierLabel as tierLabel,
  type WhatsAppStatusKind,
} from "@dolfin/lib";
import { AlertTriangle, MessageCircle } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import { toastApiError } from "@/lib/api-toast";
import { formatDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

type ConnectionState = components["schemas"]["WhatsAppConnectionStateResponse"];
type Connection = components["schemas"]["WhatsAppConnectionResponse"];
type Template = components["schemas"]["WhatsAppTemplateResponse"];
type Events = components["schemas"]["WhatsAppEventsResponse"];
type ConnectRequest = components["schemas"]["ConnectWhatsAppRequest"];
type ProblemDetails = { title?: string | null; detail?: string | null };

/** Portal has no module context, so a 403 (Module.NotEntitled) on GET means no WhatsApp module and the tab hides. */
export function useWhatsAppConnection() {
  const { activeClientId } = useAuth();
  const [state, setState] = useState<ConnectionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [notEntitled, setNotEntitled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState(0);

  // Switching Client starts again from nothing, so one Client's number is never shown (or acted on) under another.
  useEffect(() => {
    setState(null);
    setNotEntitled(false);
    setError(null);
    setLoading(!!activeClientId);
  }, [activeClientId]);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setError(null);
    (async () => {
      const { data, response } = await api.GET("/v1/whatsapp/connection", {});
      if (cancelled) return;
      setNotEntitled(response.status === 403);
      if (data) setState(data);
      else if (response.status !== 403) setError("We couldn't load your WhatsApp connection.");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeClientId, reloadCount]);

  const reload = useCallback(() => setReloadCount((n) => n + 1), []);
  // A save that returns the updated connection shows it at once, without a refetch.
  const update = useCallback(
    (connection: Connection) => setState((current) => (current ? { ...current, connection } : current)),
    [],
  );
  return { state, loading, notEntitled, error, reload, update };
}

export type WhatsAppConnectionQuery = ReturnType<typeof useWhatsAppConnection>;

const tone: Record<WhatsAppStatusKind, string> = {
  good: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  warn: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  bad: "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400",
  muted: "border-border bg-muted text-muted-foreground",
};

function StatusPill({ kind, children }: { kind: WhatsAppStatusKind; children: React.ReactNode }) {
  return (
    <Badge variant="outline" className={tone[kind]}>
      {children}
    </Badge>
  );
}

function TemplateBadge({ template }: { template: Template | undefined }) {
  if (!template?.status) return <span className="text-xs text-muted-foreground">-</span>;
  const s = WHATSAPP_TEMPLATE_STATUS[template.status];
  return <StatusPill kind={s.kind}>{s.label}</StatusPill>;
}

function TemplatesCard({ templates }: { templates: Template[] }) {
  const byKey = new Map(templates.map((t) => [t.key, t]));
  // Present only when the Client brings its own payment link: one per stage, in its WhatsApp tone.
  const ownLink = stages.flatMap((s) => {
    const key = ownLinkTemplateKey(s.key, byKey.keys());
    return key ? [byKey.get(key)!] : [];
  });
  const known = new Set([
    BILL_APPROVAL_TEMPLATE_KEY,
    ...stages.flatMap((s) => tones.map((t) => arTemplateKey(s.key, t))),
    ...ownLink.map((t) => t.key),
  ]);
  const others = templates.filter((t) => !known.has(t.key));
  const approved = templates.filter((t) => t.status === "Approved").length;
  const problems = templates.filter((t) => t.rejectionReason);
  const approval = byKey.get(BILL_APPROVAL_TEMPLATE_KEY);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Message templates
          {templates.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              {approved} of {templates.length} approved
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Meta reviews every message before it can be sent, usually within a day. Until a message is
          approved, anything that needs it goes by email instead.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Templates are being created on your WhatsApp account. Check back in a few minutes.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoices and reminders</TableHead>
                {tones.map((t) => (
                  <TableHead key={t} className="capitalize">{t}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {stages.map((stage) => (
                <TableRow key={stage.key}>
                  <TableCell className="font-medium">{stage.label}</TableCell>
                  {tones.map((t) => (
                    <TableCell key={t}>
                      <TemplateBadge template={byKey.get(arTemplateKey(stage.key, t))} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell className="font-medium">Bill needs approval</TableCell>
                <TableCell colSpan={3}>
                  <TemplateBadge template={approval} />
                </TableCell>
              </TableRow>
              {ownLink.length > 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableHead colSpan={4}>On your payment link (used for every invoice while you bring your own link)</TableHead>
                </TableRow>
              )}
              {ownLink.map((template) => (
                <TableRow key={template.key}>
                  <TableCell className="font-medium">{templateLabel(template.key)}</TableCell>
                  <TableCell colSpan={3}>
                    <TemplateBadge template={template} />
                  </TableCell>
                </TableRow>
              ))}
              {others.map((t) => (
                <TableRow key={`${t.key}-${t.language}`}>
                  <TableCell className="font-mono text-xs">{t.key}</TableCell>
                  <TableCell colSpan={3}>
                    <TemplateBadge template={t} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {problems.length > 0 && (
          <div className="flex flex-col gap-2 rounded-md border border-rose-500/30 bg-rose-500/5 p-3">
            <p className="text-sm font-medium">Why Meta rejected some messages</p>
            <ul className="flex flex-col gap-1 text-sm">
              {problems.map((t) => (
                <li key={`${t.key}-${t.language}`}>
                  <span className="font-medium">{templateLabel(t.key)}:</span>{" "}
                  <span className="text-muted-foreground">{t.rejectionReason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EventsCard({ connection, isAdmin, onSaved }: { connection: Connection; isAdmin: boolean; onSaved: (c: Connection) => void }) {
  const [saving, setSaving] = useState<keyof Events | null>(null);
  const statusByKey = new Map((connection.templates ?? []).map((t) => [t.key, t.status]));

  async function toggle(key: keyof Events, on: boolean) {
    setSaving(key);
    const { data, error } = await api.PATCH("/v1/whatsapp/connection/events", { body: { [key]: on } });
    setSaving(null);
    if (error || !data) {
      toastApiError(error, "We couldn't save that change.");
      return;
    }
    onSaved(data);
    toast.success(on ? "Now sent on WhatsApp." : "Now sent by email only.");
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Messages sent on WhatsApp</CardTitle>
        <CardDescription>
          Choose which messages go out on WhatsApp from this number to customers and colleagues who agreed to it.
          A message you switch off goes by email instead. Organisations send in different tones, so each message shows the least
          advanced review of its three tones, or of your own payment link's message when you bring one.
          {!isAdmin && " You need Admin or Owner role to change these."}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-0">
        {WHATSAPP_EVENTS.map((e, i) => {
          const review = whatsAppEventReview(e.stage, statusByKey);
          const status = review && WHATSAPP_TEMPLATE_STATUS[review];
          const id = `whatsapp-event-${e.key}`;
          return (
            <div key={e.key} className={cn("flex items-center justify-between gap-4 py-3", i > 0 && "border-t")}>
              <div className="flex flex-col gap-1">
                <Label htmlFor={id}>{e.label}</Label>
                <p className="text-xs text-muted-foreground">{e.description}</p>
              </div>
              <div className="flex items-center gap-3">
                {status && <StatusPill kind={status.kind}>{status.label}</StatusPill>}
                <Switch
                  id={id}
                  checked={connection.events[e.key]}
                  disabled={!isAdmin || saving !== null}
                  onCheckedChange={(on) => toggle(e.key, on)}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function Fact({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {hint && <InfoHint label={label}>{hint}</InfoHint>}
      </dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

/** Settings -> WhatsApp: connect a WhatsApp Business number via Meta's Embedded Signup (v4), then see its standing and template reviews. */
export function WhatsAppSettingsSection({ query }: { query: WhatsAppConnectionQuery }) {
  const { activeClient } = useAuth();
  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";
  const { state, loading, error, reload, update } = query;
  const signup = state?.embeddedSignup ?? null;
  // Test environments: connecting gives the number Twilio's WhatsApp sandbox at once, with no Facebook window.
  const sandboxNumber = state?.sandboxNumber ?? null;
  const [joiningSandbox, setJoiningSandbox] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [phone, setPhone] = useState("");

  // A reconnect is usually for the same number.
  const knownNumber = state?.connection?.displayPhoneNumber;
  useEffect(() => {
    if (knownNumber) setPhone((current) => current || knownNumber);
  }, [knownNumber]);

  // Registration with WhatsApp takes a few minutes: look again until it settles.
  const registering = state?.connection?.status === "Connecting";
  useEffect(() => {
    if (!registering) return;
    const timer = setInterval(reload, 10_000);
    return () => clearInterval(timer);
  }, [registering, reload]);

  const connect = useCallback(
    async (body: ConnectRequest) => {
      const { data, error: apiError, response } = await api.POST("/v1/whatsapp/connection", { body });
      if (!data) {
        const problem = apiError as ProblemDetails | undefined;
        const message = response.status === 403
          ? "Only Client admins can connect a WhatsApp number."
          : problem?.detail || problem?.title || "We couldn't connect your WhatsApp number. Please try again.";
        toast.error(message);
        return message;
      }
      if (data.status === "Connected") toast.success("WhatsApp connected");
      else toast.success("WhatsApp number added", { description: "It is being registered with WhatsApp, which takes a few minutes." });
      reload();
      return null;
    },
    [reload],
  );
  const onCancel = useCallback((message: string) => toast.info(message), []);
  const { signingUp, connecting, busy, canStart, error: connectError, start: startSignup } =
    useEmbeddedSignup(signup, isAdmin, connect, onCancel);

  async function connectSandbox() {
    setJoiningSandbox(true);
    try {
      await connect({});
    } finally {
      setJoiningSandbox(false);
    }
  }

  async function disconnect() {
    setDisconnecting(true);
    const result = await api.DELETE("/v1/whatsapp/connection", {});
    setDisconnecting(false);
    if (!result.data) {
      await toastApiError(result, "Couldn't disconnect WhatsApp");
      return;
    }
    setConfirmOpen(false);
    toast.success("WhatsApp disconnected", {
      description: "Approvals, invoices and reminders now go by email.",
    });
    reload();
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading WhatsApp…
        </CardContent>
      </Card>
    );
  }

  // A failed refresh keeps the page that is already showing; the error state is for a first load that failed.
  if (!state) {
    return (
      <Card>
        <CardContent className="flex items-center justify-between gap-4 py-8">
          <p className="text-sm text-destructive">{error ?? "We couldn't load your WhatsApp connection."}</p>
          <Button variant="outline" size="sm" onClick={reload}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const connection: Connection | null =
    state.connection && state.connection.status !== "Revoked" ? state.connection : null;
  const revokedAt = state.connection?.status === "Revoked" ? state.connection.revokedAt : null;

  const phoneValid = isInternationalNumber(phone);
  // Signup does not tell us the number, so it is asked for first; it must be the one verified in the Facebook window.
  const connectButton = (label: string, variant: "default" | "outline" = "default") =>
    isAdmin && sandboxNumber ? (
      <Button onClick={connectSandbox} disabled={joiningSandbox} variant={variant}>
        {joiningSandbox && <Spinner className="size-4" />}
        {joiningSandbox ? "Connecting…" : label}
      </Button>
    ) : isAdmin && signup ? (
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex flex-col gap-1.5 sm:w-64">
          <Label htmlFor="whatsapp-number">WhatsApp Business number</Label>
          <Input
            id="whatsapp-number"
            inputMode="tel"
            placeholder="+44 7700 900123"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={busy}
          />
        </div>
        <Button onClick={() => startSignup(phone.trim())} disabled={!canStart || !phoneValid} variant={variant}>
          {busy && <Spinner className="size-4" />}
          {connecting ? "Connecting…" : signingUp ? "Finish setup in the Facebook window…" : label}
        </Button>
      </div>
    ) : null;

  const errorBlock = connectError && (
    <div role="alert" className="flex items-start gap-2 rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-sm">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-rose-600 dark:text-rose-400" />
      <div className="flex flex-col gap-0.5">
        <span className="font-medium">WhatsApp could not be connected</span>
        <span className="text-muted-foreground">{connectError}</span>
      </div>
    </div>
  );

  const adminNote = !isAdmin && (
    <p className="text-xs text-muted-foreground">Only Client admins can connect or disconnect WhatsApp.</p>
  );

  if (!connection) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="size-5 text-muted-foreground" />
            WhatsApp
          </CardTitle>
          <CardDescription>
            Send bill approvals on WhatsApp from your own business number, and invoices and payment reminders for
            every organisation you serve, each under the organisation's name.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          {revokedAt && (
            <p className="text-sm text-muted-foreground">
              WhatsApp was disconnected on {formatDate(revokedAt)}. Messages go by email until you connect a
              number again.
            </p>
          )}
          <ul className="flex flex-col gap-2 text-sm">
            <li>
              <span className="font-medium">Your own number.</span>{" "}
              <span className="text-muted-foreground">
                Messages come from a WhatsApp Business number you own, and it stays yours if you disconnect.
              </span>
            </li>
            <li>
              <span className="font-medium">Your name on every message.</span>{" "}
              <span className="text-muted-foreground">
                Customers and colleagues see your business name, as approved by Meta.
              </span>
            </li>
            <li>
              <span className="font-medium">Billed by Meta.</span>{" "}
              <span className="text-muted-foreground">
                Meta charges your WhatsApp usage directly to the payment method you add in WhatsApp Manager.
              </span>
            </li>
          </ul>
          {errorBlock}
          {signup || sandboxNumber ? (
            <div className="flex flex-col items-start gap-2">
              {connectButton("Connect WhatsApp number")}
              {isAdmin && (
                <p className="text-xs text-muted-foreground">
                  {sandboxNumber
                    ? `Test environment: this connects Twilio's WhatsApp sandbox (${sandboxNumber}) straight away. A phone gets its messages once it has sent the sandbox's join code to that number.`
                    : "A Facebook window opens to sign in, pick or create your business and verify the number."}
                </p>
              )}
              {adminNote}
            </div>
          ) : (
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              WhatsApp is not available in this environment.
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const quality = WHATSAPP_QUALITY[connection.qualityRating ?? ""];
  const needsAttention = connection.status === "NeedsAttention";

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="size-5 text-muted-foreground" />
            WhatsApp
            {needsAttention ? (
              <StatusPill kind="warn">Needs attention</StatusPill>
            ) : registering ? (
              <StatusPill kind="muted">Registering</StatusPill>
            ) : (
              <StatusPill kind="good">Connected</StatusPill>
            )}
          </CardTitle>
          <CardDescription>
            Bill approvals, and every organisation's invoices and reminders, go out on WhatsApp from this number
            when the recipient has agreed to it. Everyone else still gets email.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          {registering && (
            <div role="status" className="flex items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              <Spinner className="size-4" />
              WhatsApp is registering this number, which usually takes a few minutes. Messages go by email until it
              is ready.
            </div>
          )}
          {needsAttention && (
            <div role="alert" className={cn("flex flex-col gap-3 rounded-md border p-3 text-sm sm:flex-row sm:items-center sm:justify-between", tone.warn)}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">Messages are going by email until this is fixed</span>
                  <span className="text-foreground/80">
                    {connection.statusReason || "Meta reported a problem with this number."}
                  </span>
                </div>
              </div>
              {connectButton("Reconnect", "outline")}
            </div>
          )}
          {errorBlock}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
            <Fact label="Number">{connection.displayPhoneNumber ?? "-"}</Fact>
            <Fact label="Business name" hint="The name Meta approved for this number. It is what recipients see.">
              {connection.verifiedName ?? "-"}
            </Fact>
            <Fact label="Connected">{formatDate(connection.connectedAt)}</Fact>
            <Fact
              label="Quality"
              hint="Meta's rating of this number, based on how recipients respond to recent messages. A low rating can reduce your messaging limit."
            >
              {quality ? <StatusPill kind={quality.kind}>{quality.label}</StatusPill> : <span className="text-muted-foreground">Not rated yet</span>}
            </Fact>
            <Fact
              label="Messaging limit"
              hint="Meta's daily limit on how many people this number can start new conversations with. It rises as you send more messages with good quality."
            >
              {tierLabel(connection.messagingLimitTier)}
            </Fact>
          </dl>

          {isAdmin ? (
            <div className="flex justify-end border-t pt-4">
              <Button variant="outline" onClick={() => setConfirmOpen(true)} disabled={busy}>
                Disconnect
              </Button>
            </div>
          ) : (
            adminNote
          )}
        </CardContent>
      </Card>

      <EventsCard connection={connection} isAdmin={isAdmin} onSaved={update} />

      <TemplatesCard templates={connection.templates ?? []} />

      <Dialog open={confirmOpen} onOpenChange={(open) => !disconnecting && setConfirmOpen(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Disconnect WhatsApp?</DialogTitle>
            <DialogDescription>
              Bill approvals, invoices and reminders will go by email from now on. Your number stays
              registered on your own WhatsApp Business account, so you can connect it again later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={disconnecting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={disconnect} disabled={disconnecting}>
              {disconnecting && <Spinner className="size-4" />}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
